import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import * as fromPageAlertActions from '@rci/self-serve-fido/shared/page-alert/state/actions';
import { PageContentService } from '@rci/self-serve-fido/shared/page-content/page-content.service';
import { AlertFacadeService, AppAlert, AppAlertType, upsertAlert } from '@rogers/self-serve-common/alert';
import { Observable } from 'rxjs';
import { BannerInfo } from '@rogers/self-serve-common/page-content';
import { Cta } from '@rogers/cdk/assembled/cta';

@Injectable()
export class PageAlertsEffects {
  loadPageAlerts$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPageAlertActions.PageAlertActionTypes.GetPageAlert),
        withLatestFrom(this.alertFacadeService.getAlerts$('page')),
        filter(([{ page }, appAlerts]) => {
          return !appAlerts.some(alert => alert.uniqueAlertId === `${page}_page_alert`);
        }),
        mergeMap(([{ page }]) => {
          return this.pageContentService.getPageContent$(page).pipe(
            map(({ banners }) => {
              return this.generatePageAlert(page, banners);
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly alertFacadeService: AlertFacadeService,
    private readonly pageContentService: PageContentService,
  ) {}

  private generatePageAlert(page: string, banners: BannerInfo[]): Action {
    const pageAlert = banners.find(banner => banner.promoID === 'alert');
    if (!pageAlert) {
      /**
       * Since null can't be returned from an effect that's why I have created an equivalent action for null,
       * that basically does nothing. If null is returned that would throw error.
       */
      return new fromPageAlertActions.NoOpAction();
    }
    const alert: AppAlert = {
      key: `${page}_page_alert`,
      uniqueAlertId: `${page}_page_alert`,
      type: AppAlertType.page,
      alertData: {
        copy: {
          title: pageAlert.copy.header,
          body: pageAlert.copy.body,
          closeLabel: pageAlert.closeAltText,
        },
        cta: pageAlert.cta
          ? [
              {
                copy: (pageAlert.cta as Cta).copy,
                val: (pageAlert.cta as Cta).val,
                alt: (pageAlert.cta as Cta).alt,
                target: (pageAlert.cta as Cta).target,
              },
            ]
          : null,
        showCloseButton: pageAlert.copy.dismissable === 'true',
        variant: pageAlert.backgroundColor as 'success' | 'error' | 'warning' | 'info',
      },
      iconName: pageAlert.copy.icon,
    };
    return upsertAlert({ alert });
  }
}
